export function calculateSimilarity(str1, str2) {
  const stopWords = new Set([
    'a',
    'an',
    'the',
    'and',
    'or',
    'but',
    'if',
    'then',
    'else',
    'so',
    'because',
    'of',
    'in',
    'on',
    'at',
    'to',
    'from',
    'by',
    'for',
    'with',
    'about',
    'as',
    'is',
    'are',
    'was',
    'were',
    'be',
    'been',
    'being',
    'have',
    'has',
    'had',
    'do',
    'does',
    'did',
    'not',
    'no',
    'nor',
    'too',
    'very',
    'that',
    'this',
    'these',
    'those',
    'which',
    'who',
    'whom',
    'whose',
    'when',
    'where',
    'why',
    'how',
    'until',
    'while',
    'during',
    'per',
    'via',
    'into',
    'onto',
    'over',
    'under',
    'between',
    'among',
    'through',
    'within',
    'without',
    'mine',
    'your',
    'yours',
    'his',
    'her',
    'its',
    'our',
    'their',
  ]);

  const stem = (w) => {
    if (!w) return w;
    if (w.length <= 3) return w;

    const irregulars = {
      people: 'person',
      children: 'child',
      feet: 'foot',
      teeth: 'tooth',
      men: 'man',
      women: 'woman',
      mice: 'mouse',
      geese: 'goose',
    };
    if (irregulars[w]) return irregulars[w];

    if (w.endsWith('ization')) return w.slice(0, -7) + 'ize';
    if (w.endsWith('ational')) return w.slice(0, -7) + 'ate';
    if (w.endsWith('fulness')) return w.slice(0, -7) + 'ful';
    if (w.endsWith('iveness')) return w.slice(0, -7) + 'ive';
    if (w.endsWith('tional')) return w.slice(0, -6);
    if (w.endsWith('biliti')) return w.slice(0, -6) + 'ble';
    if (w.endsWith('less')) return w.slice(0, -4);
    if (w.endsWith('able')) return w.slice(0, -4);
    if (w.endsWith('ible')) return w.slice(0, -4);
    if (w.endsWith('ment')) return w.slice(0, -4);
    if (w.endsWith('ness')) return w.slice(0, -4);
    if (w.endsWith('ously')) return w.slice(0, -4);
    if (w.endsWith('fully')) return w.slice(0, -4);
    if (w.endsWith('ing')) {
      const base = w.slice(0, -3);
      if (base.length >= 3) {
        if (base.length > 2 && base[base.length - 1] === base[base.length - 2]) {
          return base.slice(0, -1);
        }
        return base;
      }
    }
    if (w.endsWith('ed')) {
      const base = w.slice(0, -2);
      if (base.length >= 3) {
        if (base.length > 2 && base[base.length - 1] === base[base.length - 2]) {
          return base.slice(0, -1);
        }
        return base;
      }
    }
    if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';
    if (w.endsWith('ves') && w.length > 4) return w.slice(0, -3) + 'f';
    if (w.endsWith('oes')) return w.slice(0, -2);
    if (/(ches|shes|ses|xes|zes)$/.test(w)) return w.slice(0, -2);
    if (w.endsWith('es')) return w.slice(0, -2);
    if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
    if (w.endsWith('ly')) {
      // naive handling: prefer removing only 'y' when that produces a likely base (helps "curly" -> "curl")
      // otherwise remove 'ly'
      const withoutY = w.slice(0, -1);
      if (withoutY.length > 2 && withoutY.endsWith('l')) return withoutY;
      return w.slice(0, -2);
    }

    return w;
  };

  const normalizeTokens = (s) =>
    (s || '')
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s']/gi, ' ')
      .split(/\s+/)
      .filter(Boolean)
      .map((t) => {
        const tok = t.replace(/^'+|'+$/g, '');
        return stem(tok);
      })
      .filter((t) => t && !stopWords.has(t));

  const tokens1 = normalizeTokens(str1);
  const tokens2 = normalizeTokens(str2);

  if (tokens1.length === 0 && tokens2.length === 0) return 1;
  if (tokens1.length === 0 || tokens2.length === 0) return 0;

  const freq = (tokens) => {
    const m = new Map();
    for (const t of tokens) m.set(t, (m.get(t) || 0) + 1);
    return m;
  };

  const f1 = freq(tokens1);
  const f2 = freq(tokens2);

  // Levenshtein distance (iterative, small footprint)
  const levenshtein = (a, b) => {
    if (a === b) return 0;
    const m = a.length,
      n = b.length;
    if (m === 0) return n;
    if (n === 0) return m;
    let prev = new Array(n + 1);
    for (let j = 0; j <= n; j++) prev[j] = j;
    for (let i = 1; i <= m; i++) {
      let curr = [i];
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        curr[j] = Math.min(prev[j] + 1, curr[j - 1] + 1, prev[j - 1] + cost);
      }
      prev = curr;
    }
    return prev[n];
  };

  // compute norms
  let norm1 = 0;
  let norm2 = 0;
  for (const v of f1.values()) norm1 += v * v;
  for (const v of f2.values()) norm2 += v * v;
  if (norm1 === 0 || norm2 === 0) return 0;

  // build dot with fuzzy matching: exact matches + best approximate matches (dist <= 2)
  let dot = 0;
  const f2Unused = new Set(f2.keys());

  for (const [k1, v1] of f1.entries()) {
    if (f2.has(k1)) {
      dot += v1 * f2.get(k1);
      f2Unused.delete(k1);
      continue;
    }
    // find best unused match in f2
    let bestKey = null;
    let bestDist = Infinity;
    for (const k2 of f2Unused) {
      const d = levenshtein(k1, k2);
      if (d < bestDist) {
        bestDist = d;
        bestKey = k2;
        if (d === 0) break;
      }
    }
    if (bestKey !== null && bestDist <= 2) {
      const v2 = f2.get(bestKey);
      const weight = bestDist === 1 ? 0.9 : 0.7; // distance 1 ~ very similar, distance 2 ~ somewhat similar
      dot += v1 * v2 * weight;
      f2Unused.delete(bestKey);
    }
  }

  return dot / (Math.sqrt(norm1) * Math.sqrt(norm2));
}
